/**
 * useConnection.js — is this device online, and is anything waiting to sync?
 *
 * WHY THIS MATTERS MORE THAN IT LOOKS
 * Firestore's persistent cache makes the application work offline, which is
 * genuinely useful in a shop with a bad connection — and genuinely dangerous
 * if nobody says so. A save made offline succeeds: the toast appears, the
 * invoice is on screen, and the write sits in a queue on that one device. If
 * the user then closes the tab on a phone that is out of storage, or bills the
 * same invoice again from the till, they had no way of knowing.
 *
 * So the shell shows a persistent indicator whenever the device is offline.
 * Not a toast — a toast is for something that happened, and being offline is a
 * STATE that lasts until it stops.
 *
 * WHAT THIS CAN AND CANNOT KNOW — READ THIS BEFORE TRUSTING `isOnline`
 * `navigator.onLine` reports whether the device has a network interface, and
 * NOTHING MORE. A captive portal at a railway station is "online" and can
 * reach nothing; a laptop on a wifi network whose upstream is down is
 * "online"; a phone with one bar is "online" while every request times out.
 *
 * This hook does not detect any of those. It reports the browser's own flag
 * and the user's deliberate pause, and it is deliberately named for what it
 * observes rather than for what a caller might wish it meant. Detecting real
 * reachability needs a probe against Firestore itself — watching
 * `snapshot.metadata.fromCache` on a document the app already subscribes to is
 * the cheap way to do it — and until that exists, a FALSE "online" is the
 * failure mode to expect. It is the safe direction: the indicator under-warns
 * rather than crying wolf, and Firestore queues the writes either way.
 */

import { useCallback, useEffect, useState } from 'react';
import { disableNetwork, enableNetwork } from 'firebase/firestore';
import { db } from '../firebase';

export function useConnection() {
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine !== false
  );
  /** Set when the user deliberately goes offline, which is a different state. */
  const [isPaused, setIsPaused] = useState(false);
  const [wentOfflineAt, setWentOfflineAt] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const goOnline = () => {
      setIsOnline(true);
      setWentOfflineAt(null);
    };
    const goOffline = () => {
      setIsOnline(false);
      /* Recorded once, on the transition. Recomputing it on every render would
         make "offline since 14:02" tick forward to now. */
      setWentOfflineAt((existing) => existing || new Date());
    };

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  /**
   * Work offline on purpose.
   *
   * Useful in a shop that knows its connection is about to go — a market
   * stall, a delivery van — because it stops every read waiting on a timeout
   * before falling back to the cache, which is what makes a bad connection
   * feel worse than no connection.
   */
  /* The flag is only set once the SDK has actually done it. Setting it first
     and letting the promise reject would leave the pill claiming a state the
     database is not in — which is exactly the lie this whole hook exists to
     avoid. The rejection is re-thrown so the caller can say something. */
  const pause = useCallback(async () => {
    await disableNetwork(db);
    setIsPaused(true);
  }, []);

  const resume = useCallback(async () => {
    await enableNetwork(db);
    setIsPaused(false);
  }, []);

  /* Reconnect on unmount would be wrong — the user asked for offline and it
     outlives this component — but a pause that survives a reload with no way
     back would trap them. `disableNetwork` is per-SDK-instance and does not
     persist across a reload, so a refresh always comes back online. Stated
     because it is the kind of thing somebody would otherwise try to fix. */

  return {
    isOnline: isOnline && !isPaused,
    isPaused,
    /** True when the browser says there is no network at all. */
    isDisconnected: !isOnline,
    wentOfflineAt,
    pause,
    resume,
  };
}

export default useConnection;
